/* eslint-disable @typescript-eslint/no-shadow */
import React, { ChangeEvent, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { ReactComponent as InformationIcon } from "../../assets/icons/Information.svg";
import DialogMessage from "../../components/DialogMessage";
import { DeleteIcon } from "../../components/Icons";
import { PromisifiedDialog } from "../../components/PromisifiedDialog/PromisifiedDialog";
import { IPromisifiedDialogProps } from "../../components/PromisifiedDialog/IPromisifiedDialog";
import InputLabelTextArea from "../../components/InputLabelTextArea";
import DialogMessageProps from "../../model/DialogMessageProps";
import { Routes } from "../../model/Enums/RoutesEnum";
import {
    getLabelTipoCartaoEnum,
    TipoCartaoEnum,
    isOvertimeCard,
} from "../../model/Enums/TipoCartaoEnum";
import { StatusWorkflowEnum } from "../../model/Enums/StatusWorkflowEnum";
import { TypeDialogMessageEnum } from "../../model/Enums/TypeDialogMessageEnum";
import GenericUtils from "../../utils/GenericUtils";

import ButtonStyles from "../../styles/components/ButtonStyles";
import {
    StyledCard,
    StyledGroupCardContainer,
    StyledTitleContainer,
} from "../../styles/pages/Schedule/ScheduleStyle";

const { BtnLink } = ButtonStyles;

interface IScheduleCardsProps {
    id: string;
    title: string;
    subTitle: string;
    hour: string;
    cardTypeId: TipoCartaoEnum;
    cards: any[];
    removeCard: (card: any) => void;
    removeJustifiedCard: (cartaoId: number, justification: string) => void;
}

interface IDialogMessageOvertimeProps extends IPromisifiedDialogProps {
    justification: string;
    show: boolean;
    hasWorkflow: boolean;
}

const initialDialogMessageOvertimeProps: IDialogMessageOvertimeProps = {
    justification: "",
    show: false,
    text: "Existe um processo de aprovação em andamento para este cartão. Você precisa fornecer uma justificativa para excluir o cartão de Hora Letiva RE, deseja continuar?",
    width: 400,
    acceptButtonText: "Continuar",
    cancelButtonText: "Cancelar",
    hasWorkflow: false,
};

const ScheduleCards: React.FC<IScheduleCardsProps> = ({
    id,
    title,
    subTitle,
    hour,
    cardTypeId,
    cards,
    removeCard,
    removeJustifiedCard,
}) => {
    const location = useLocation();
    const pathLocation = location.pathname.split("/");
    const mySchedulePage = location.pathname === "/minha-agenda";
    const isEditSchedulePage = GenericUtils.getActualPage() === "editar-agenda";
    const user = GenericUtils.getUser();

    const [dialogMessageProps, setDialogMessageProps] =
        useState<DialogMessageProps>({} as DialogMessageProps);

    const [dialogMessageOvertimeProps, setDialogMessageOvertimeProps] =
        useState<IDialogMessageOvertimeProps>(
            initialDialogMessageOvertimeProps
        );

    const isValidAcceptButton =
        user.isDac && !mySchedulePage
            ? !dialogMessageOvertimeProps.justification.length
            : false;

    const isSpecialCard = (cardType: TipoCartaoEnum) => {
        return (
            cardType === TipoCartaoEnum.Antecipacao ||
            cardType === TipoCartaoEnum.Intrajornada ||
            cardType === TipoCartaoEnum.Substituicao ||
            cardType === TipoCartaoEnum.Reposicao ||
            cardType === TipoCartaoEnum.AtividadesAvulsas ||
            cardType === TipoCartaoEnum.HoraExtraImportacao ||
            cardType === TipoCartaoEnum.HoraExtraTela
        );
    };

    const handleCloseDialogQuestion = (callback: any) => {
        const cardToRemove = cards.find(
            (card) => card.cartaoId === callback.cardId
        );

        setDialogMessageProps({} as DialogMessageProps);
        if (callback?.confirmButton) {
            if (cardTypeId === TipoCartaoEnum.HoraExtraTela) {
                removeJustifiedCard(
                    cards[0].cartaoId,
                    dialogMessageOvertimeProps.justification
                );
            } else {
                removeCard(cardToRemove);
            }
        }
    };

    const handleJustificationChange = (
        event: ChangeEvent<{ value: unknown }>
    ) => {
        const dialogObject: IDialogMessageOvertimeProps = {
            ...dialogMessageOvertimeProps,
            justification: event.target.value as string,
        };

        setDialogMessageOvertimeProps(dialogObject);
    };

    const handleOnActionDialogRemove = (callback: boolean) => {
        if (callback) {
            removeJustifiedCard(
                cards[0].cartaoId,
                dialogMessageOvertimeProps.justification
            );
            setDialogMessageOvertimeProps(initialDialogMessageOvertimeProps);
        } else {
            setDialogMessageOvertimeProps({
                ...dialogMessageOvertimeProps,
                show: false,
            });
        }
    };

    const getOvertimeDialogText = (
        isEditSchedulePage: boolean,
        hasWorkflow: boolean
    ) => {
        if (!isEditSchedulePage) {
            if (hasWorkflow) {
                return "Existe um processo de aprovação em andamento para este cartão. Você pode fornecer uma justificativa para excluir o cartão de Hora Letiva RE, deseja continuar?";
            }

            return "O cartão será removido. Você pode fornecer uma justificativa para excluir o cartão de Hora Letiva RE, deseja continuar?";
        }

        if (hasWorkflow) {
            return "Existe um processo de aprovação em andamento para este cartão. Você precisa fornecer uma justificativa para excluir o cartão de Hora Letiva RE, deseja continuar?";
        }

        return "O cartão será removido. Você precisa fornecer uma justificativa para excluir o cartão de Hora Letiva RE, deseja continuar?";
    };

    const handleClickRemoveCard = (
        cardType: TipoCartaoEnum,
        statusWorkflow: StatusWorkflowEnum,
        cardId: number
    ) => {
        if (isOvertimeCard(null, cardType)) {
            setDialogMessageOvertimeProps({
                ...dialogMessageOvertimeProps,
                show: true,
                hasWorkflow: !!statusWorkflow,
                text: getOvertimeDialogText(
                    isEditSchedulePage,
                    !!statusWorkflow
                ),
            });
        } else {
            setDialogMessageProps({
                show: true,
                type: TypeDialogMessageEnum.Question,
                title: isSpecialCard(cardTypeId)
                    ? `Excluir cartão de ${getLabelTipoCartaoEnum(cardTypeId)}?`
                    : "Deseja Excluir cartão?",
                text: "O cartão será removido.",
                textButton: "Excluir",
                textButtonClose: "Cancelar",
                onClose: {
                    handleCloseDialogQuestion,
                },
                cardId,
            });
        }
    };

    const dialogMessageRemoveOvertime = dialogMessageOvertimeProps.show && (
        <PromisifiedDialog
            text={dialogMessageOvertimeProps.text}
            width={dialogMessageOvertimeProps.width}
            acceptButtonText={dialogMessageOvertimeProps.acceptButtonText}
            acceptButtonDisabled={isValidAcceptButton}
            cancelButtonText={dialogMessageOvertimeProps.cancelButtonText}
            onResolve={handleOnActionDialogRemove}
            onReject={handleOnActionDialogRemove}
        >
            <InputLabelTextArea
                placeholder="Justificativa"
                value={dialogMessageOvertimeProps.justification}
                maxLength={500}
                viewLengthInfo
                clear
                onChange={handleJustificationChange}
            />
        </PromisifiedDialog>
    );

    const dialogQuestion = dialogMessageProps.show && (
        <DialogMessage
            type={dialogMessageProps.type}
            show={dialogMessageProps.show}
            title={dialogMessageProps.title}
            text={dialogMessageProps.text}
            textButton={dialogMessageProps.textButton}
            textButtonClose={dialogMessageProps.textButtonClose}
            onClose={handleCloseDialogQuestion}
            cardId={dialogMessageProps.cardId}
        />
    );

    const dialogs = (
        <>
            {dialogQuestion}
            {dialogMessageRemoveOvertime}
        </>
    );

    useEffect(() => {
        return () => {
            setDialogMessageOvertimeProps(initialDialogMessageOvertimeProps);
        };
    }, []);

    return (
        <div key={id} id={id} className="d-flex justify-content-center">
            <StyledGroupCardContainer>
                <div key={id} style={{ padding: "16px 10px" }}>
                    <StyledTitleContainer style={{ fontSize: 15 }}>
                        {title}
                    </StyledTitleContainer>
                    <StyledTitleContainer
                        style={{ fontSize: 12, fontWeight: 400 }}
                    >
                        {subTitle}
                    </StyledTitleContainer>
                    <div className="d-flex justify-content-between">
                        {cardTypeId !== TipoCartaoEnum.Intrajornada && (
                            <>
                                <StyledTitleContainer
                                    style={{
                                        fontSize: 13,
                                        lineHeight: "15.6px",
                                    }}
                                >
                                    Total de horas
                                </StyledTitleContainer>
                                <StyledTitleContainer
                                    style={{
                                        fontSize: 15,
                                        lineHeight: "22.5px",
                                        color: "#9654FF",
                                    }}
                                >
                                    {hour} h/a
                                </StyledTitleContainer>
                            </>
                        )}
                    </div>
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 8,
                        }}
                    >
                        {cards.length ? (
                            cards.map((event) => (
                                <StyledCard
                                    className="fc-event"
                                    // title={event.title}
                                    key={event.id}
                                    color={event.color}
                                    data-duration={event.duration}
                                    data-id={event.id}
                                    style={{
                                        background: event.color,
                                        color: event.textColor,
                                    }}
                                >
                                    <div
                                        style={{
                                            fontSize: 13,
                                            color: event.textColor,
                                            marginBottom: 0,
                                            lineHeight: "15.6px",
                                            justifyContent: "end",
                                        }}
                                    >
                                        <div>{event.title}</div>
                                    </div>
                                    <div
                                        style={{
                                            fontSize: 13,
                                            color: event.textColor,
                                            marginBottom: 0,
                                            lineHeight: "15.6px",
                                            minWidth: 46,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "end",
                                        }}
                                    >
                                        <div
                                            style={{
                                                margin: 3,
                                                textAlign: "center",
                                            }}
                                        >
                                            {event.horas} h/a
                                        </div>
                                        <div
                                            style={{
                                                margin: 3,
                                            }}
                                        >
                                            {isSpecialCard(
                                                event.cartaoTipoId
                                            ) &&
                                                pathLocation[1] !==
                                                    Routes.ConsultarAgenda && (
                                                    <BtnLink
                                                        onClick={() =>
                                                            handleClickRemoveCard(
                                                                cardTypeId,
                                                                event.statusWorkflow,
                                                                event.cartaoId
                                                            )
                                                        }
                                                    >
                                                        <DeleteIcon
                                                            style={{
                                                                fill: event.textColor,
                                                                width: 14,
                                                                marginTop: 2,
                                                            }}
                                                        />
                                                    </BtnLink>
                                                )}
                                        </div>
                                    </div>
                                </StyledCard>
                            ))
                        ) : (
                            <div style={{ display: "flex" }}>
                                <InformationIcon style={{ fill: "#404040" }} />
                                <StyledTitleContainer
                                    style={{
                                        fontSize: 12,
                                        fontWeight: 600,
                                        marginLeft: 10,
                                    }}
                                >
                                    Todas as atividades já foram alocadas
                                </StyledTitleContainer>
                            </div>
                        )}
                    </div>
                </div>
            </StyledGroupCardContainer>
            {dialogs}
        </div>
    );
};

export default ScheduleCards;
